import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global, globalMenu } from "@shared/libs/Global";
import { fnSave } from "@shared/api/restApi";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SnButton from "@shared/components/SnButton";
import { showPopupModal, showResizablePopupModal } from "@widgets/CommonModal";
import ExcelUpload from "@widgets/commonpopup/ExcelUpload";
import SN_SAL_BAS_REG_001_POP_001 from "./SN_SAL_BAS_REG_001_POP_001";
import SearchPanel from "@widgets/SearchPanel";
import { parse, isValid } from "date-fns";
import { ProcessCellForExportParams, RowNode } from "ag-grid-community";
import { dateToString } from "@shared/utils/DateUtils";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";

interface Props {
  Menuprops: menuInfo;
}

// 엑셀 업로드 시 기존 데이터 여부를 판정할 PK 조합 (mas_umitem)
const UPLOAD_PK_FIELDS = [
  "cd_firm",
  "cd_module",
  "tp_um",
  "cd_exch",
  "cd_item",
  "dt_start",
];

// 엑셀 업로드 오류체크(필수값) 대상 컬럼
// 품목코드, 환종, 단가유형, 단가, 부가세포함단가, 시작일
const UPLOAD_REQUIRED_FIELDS = [
  "cd_item",
  "cd_exch",
  "tp_um",
  "um",
  "um_supply",
  "dt_start",
];

// 종료일 기본값 (행추가 / 엑셀 업로드에서 종료일을 비워둔 경우)
const DEFAULT_DT_END = "21001231";

const SN_SAL_BAS_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
    grid4: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  const [origin, setOrigin] = useState<any[]>([]);

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelDownLoad,
    onExcelUpload,
  }));

  const onSearch = async () => {
    const modifiedRows = gridRefs.current["grid1"].current.getChange();
    if (modifiedRows.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sal_bas_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
      });
      setOrigin(data[0]);
    } catch (error: any) {}
  };

  const onInsert = () => {
    try {
      return gridRefs.current["grid1"].current.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRow = (): any => {
    try {
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo.cd_firm,
        cd_module: "SAL",
        cd_user_reg: userInfo.cd_user,
        tp_um: searchInfo?.tp_um || "001",
        cd_exch: searchInfo?.cd_exch || "000",
        dt_start: today,
        dt_end: DEFAULT_DT_END,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;
    gridRefs.current["grid1"].current?.deleteSelectedRows();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();

      const requiredValuesCheck =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheck) return false;

      const masUmItemChanges = gridRefs.current["grid1"].current.getChange();
      if (masUmItemChanges.modifiedRowcount < 1) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const ischecked = await checkDt();
      if (!ischecked) return false;

      const saveData = {
        umitem: masUmItemChanges,
      };

      const ok = await fnSave("SN_SAL_BAS_REG_001_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid1"].current.resetUpdate();
      await onSearch();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  /**
   * 엑셀 다운로드 컬럼.
   * 엑셀 업로드 시트와 같은 구성(표시 컬럼)·순서(no_seq)로 내려서,
   * 받은 파일을 그대로 업로드에 붙여넣을 수 있게 한다.
   * (업로드는 붙여넣기 기준이라 컬럼 순서가 다르면 값이 밀린다)
   */
  const getDownloadColumnKeys = (): string[] =>
    [...(columns.grid1 ?? [])]
      .filter((col: any) => col?.cd_column && col.fg_visible === "Y")
      .sort((a: any, b: any) => (a.no_seq ?? 0) - (b.no_seq ?? 0))
      .map((col: any) => col.cd_column);

  const onExcelDownLoad = async () => {
    try {
      const columnKeys = getDownloadColumnKeys();

      const flatRowMap =
        gridRefs.current["grid1"].current.getFlatDataFromGrid(columnKeys);

      gridRefs.current["grid1"].current?.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
        processCellCallback: (params: ProcessCellForExportParams) => {
          const row = flatRowMap.get(params.node?.id ?? "");
          return row?.[params.column.getColId()]?.value ?? "";
        },
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onExcelUpload = async () => {
    try {
      // 업로드 시트는 이 화면의 그리드(grid1) 컬럼을 그대로 사용한다.
      const component = (
        <ExcelUpload
          Menuprops={Menuprops}
          PkFields={UPLOAD_PK_FIELDS}
          tableName="umitem" // SN_SAL_BAS_REG_001_SAVE_01 이 받는 데이터셋 키
          controllerName="SN_SAL_BAS_REG_001_SAVE_01"
          pkCheckCode="mas_umitem" // PK 중복 판정용 조회 코드
          defaultValue={{
            cd_firm: userInfo.cd_firm,
            cd_module: "SAL",
            cd_user_reg: userInfo.cd_user,
            // 종료일을 비워두고 올리면 행추가와 같은 기본값이 들어간다.
            dt_end: DEFAULT_DT_END,
          }}
          columnConfig={columns.grid1}
          requiredFields={UPLOAD_REQUIRED_FIELDS}
        />
      );

      const result = await showPopupModal({
        title: "엑셀 업로드",
        children: component,
        width: "80%",
        height: "90VH",
        okayBtnText: "저장",
      });

      if (result?.excelUpload) {
        await onSearch();
      }
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onGridBtnsClick = async (id: string) => {
    if (id === "btnApply") {
      if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

      const uniqueCdItems = Array.from(
        new Set(origin.map((row: any) => row.cd_item))
      );
      if (uniqueCdItems.length === 0) return;
      const cdItem = uniqueCdItems.join("|");

      const component = (
        <SN_SAL_BAS_REG_001_POP_001
          selectedData={{ cd_module: "SAL", cd_item: cdItem }}
        />
      );
      const result = await showResizablePopupModal({
        title: "단가일괄조정",
        children: component,
        width: "1000px",
        height: "570px",
        validation: true,
      });
      if (!result || result === "close") return;

      const headerResult = result;
      if (!headerResult) return;
      headerResult.map((result: any) => {
        const addedNode = onInsert();
        if (addedNode) {
          addedNode.setDataValue("cd_item", result.cd_item);
          addedNode.setDataValue("nm_item", result.nm_item);
          addedNode.setDataValue("dc_item_spec", result.dc_item_spec);
          addedNode.setDataValue("dc_item_unit", result.dc_item_unit);
          addedNode.setDataValue("cd_exch", result.cd_exch);
          addedNode.setDataValue("tp_um", result.tp_um);
          addedNode.setDataValue("dt_start", result.dt_from);
          addedNode.setDataValue("dt_end", result.dt_to);
          addedNode.setDataValue("um", result.um);
          addedNode.setDataValue("cd_item_cust", result.cd_item_cust);
          addedNode.setDataValue("dc_rmk", result.dc_rmk);
          addedNode.setDataValue("nm_custm", result.cd_cust_maker);
          addedNode.setDataValue("nm_custi", result.cd_cust_isu);
          addedNode.setDataValue("nm_custr", result.cd_cust_rcv);
          addedNode.setDataValue("nm_custm", result.nm_custm);
          addedNode.setDataValue("nm_custi", result.nm_custi);
          addedNode.setDataValue("nm_custr", result.nm_custr);
        }
      });
    }
  };

  const onCellValueChanged = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "um") {
      rowNode.setDataValue("um_supply", Number(rowData.um) * 1.1, "noEvent");
    }
    if (colId === "um_supply") {
      rowNode.setDataValue(
        "um",
        (Number(rowData.um_supply) * 10) / 11,
        "noEvent"
      );
    }
  };

  const checkDt = async () => {
    let renderedRows: any[] = [];
    gridRefs.current["grid1"].current.forEachNode((node: RowNode) => {
      renderedRows.push(node);
    });

    for (const node of renderedRows) {
      const dtStartStr = node.data?.dt_start;
      const dtEndStr = node.data?.dt_end;
      if (
        (dtStartStr === "" ||
          dtStartStr === null ||
          dtStartStr === undefined) &&
        (dtEndStr === "" || dtEndStr === null || dtEndStr === undefined)
      ) {
        gridRefs.current["grid1"].current.setFocusedCell(
          node?.rowIndex,
          "dt_start"
        );
        return false;
      }
      if (dtEndStr === "" || dtEndStr === null || dtEndStr === undefined) {
        continue;
      }
      const formats = [
        "yyyyMMdd",
        "yyyy-MM-dd a h:mm:ss",
        "yyyy-MM-dd HH:mm:ss",
      ];

      const dtStart = tryParseExact(dtStartStr, formats);
      const dtEnd = tryParseExact(dtEndStr, formats);

      if (!dtStart || !dtEnd) {
        return false;
      }
      if (dtEnd < dtStart) {
        showDialog(
          "alter",
          "종료일은 시작일보다 빠를 수 없습니다.\n다시 확인하여 주시기 바랍니다."
        );
        gridRefs.current["grid1"].current.setFocusedCell(
          node?.rowIndex,
          "dt_end"
        );
        return false;
      }
    }
    return true;
  };

  function tryParseExact(dateStr: string, formats: string[]): Date | null {
    for (const format of formats) {
      const parsed = parse(dateStr, format, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    }
    return null;
  }

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRow,
          onCellValueChanged: onCellValueChanged,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1">
        <SnGrid
          ref={gridRefs.current["grid1"]}
          columnConfig={grids.grid1.columns}
          rowData={grids.grid1.dataSource}
          etcProps={{
            rowSelection: "multiple",
            isEditable: true,
            title: "품목정보",
          }}
          events={grids.grid1.events}
          header={
            <div>
              <SnButton
                text="단가일괄적용"
                onClick={() => {
                  onGridBtnsClick("btnApply");
                }}
              ></SnButton>
            </div>
          }
        />
      </div>
    </div>
  );
});

export default SN_SAL_BAS_REG_001;
